import React, { memo, useCallback, useContext, useMemo } from "react";

//components
import { DeliverySectionShimmer } from "@swiggy-private/cart-ui";
import { TipWidget } from "./components/TipWidget";
import { DeliveryInstructions } from "./components/DeliveryInstructions";
import { CartDeliverySpeed } from "@cart-client/components/cart-delivery-speed";
import { MROCartDeliverySpeed } from "@cart-client/components/mro-cart/mro-cart-delivery-speed";
import { SelfDeliveryInstructions } from "./components/SelfDeliveryInstructions";
import { TabContainer } from "@swiggy-private/dls-web-core/dist/components/TabContainer";
import { SingleTab } from "./components/SingleTab";

//types
import {
    DELIVERY_SECTION_ELEMENT,
    isPopFlow,
    isToingFlow,
    NEWRELIC_TABLES,
    recordNREvent,
} from "@swiggy-private/checkout-core";
import { DeliveryRendererProps, GetDeliveryWidgetParams } from "./types";
import { DeliveryModeLottie } from "@swiggy-private/cart-ui";
import { DELIVERY_MODE_ANIMATION_URL } from "@cart-client/hooks/useDeliveryModeLottie";
import { TabBarTheme } from "@swiggy-private/dls-web-core/dist/components/TabContainer/types";

//hooks
import { useDeliveryRenderer } from "@cart-client/hooks/useDeliveryRenderer";
import { DeliverySectionContext } from "@cart-client/contexts/DeliverySectionContext";
import { DeliveryRendererCartGroupContainer } from "./styles";
import { CART_ELEMENT_IDS } from "@cart-client/components/cart-renderer/constants";
import { ElementObserver, ErrorBoundary } from "@swiggy-private/core-ui";
import { getNRErrorInfo } from "@swiggy-private/js-utils";
import { ITabBarItem } from "@swiggy-private/dls-web-core/dist/components/TabContainer/components/TabBar/types";
import { RenderingDetails } from "@swiggy-private/cart-types";
import { useEcoSaverCrouton } from "@cart-client/hooks/useEcoSaverCrouton";
import { useComputeTabContainerPadding } from "./hooks/useComputeTabContainerPadding";
import { isMROFlow } from "@cart-client/helpers/MROFoodCart";
import { useDeliveryModeLottie } from "@cart-client/hooks/useDeliveryModeLottie";
function getWidget({
    tabItem,
    setDeliverySpeedCroutonActive,
    cartTopOffset,
    setCroutonVisibility,
    canShowDeliveryInstructionCrouton,
    canShowDeliverySpeedCrouton,
    isDeliverySpeedCroutonActive,
    ecoSaverLottieSuccess,
}: GetDeliveryWidgetParams) {
    switch (tabItem.id as DELIVERY_SECTION_ELEMENT) {
        case DELIVERY_SECTION_ELEMENT.TIP:
            return <TipWidget />;
        case DELIVERY_SECTION_ELEMENT.INSTRUCTIONS:
            return (
                <DeliveryInstructions
                    cartTopOffset={cartTopOffset}
                    setCroutonVisibility={setCroutonVisibility}
                    canShowDeliveryInstructionCrouton={canShowDeliveryInstructionCrouton}
                />
            );
        case DELIVERY_SECTION_ELEMENT.DELIVERY_TYPE:
            if (isMROFlow()) {
                return (
                    <MROCartDeliverySpeed
                        isDeliverySpeedCroutonActive={isDeliverySpeedCroutonActive}
                        setDeliverySpeedCroutonActive={setDeliverySpeedCroutonActive}
                        canShowDeliverySpeedCrouton={canShowDeliverySpeedCrouton}
                        setCroutonVisibility={setCroutonVisibility}
                        cartTopOffset={cartTopOffset}
                        ecoSaverLottieSuccess={ecoSaverLottieSuccess}
                    />
                );
            }
            return (
                <CartDeliverySpeed
                    isDeliverySpeedCroutonActive={isDeliverySpeedCroutonActive}
                    setDeliverySpeedCroutonActive={setDeliverySpeedCroutonActive}
                    canShowDeliverySpeedCrouton={canShowDeliverySpeedCrouton}
                    setCroutonVisibility={setCroutonVisibility}
                    cartTopOffset={cartTopOffset}
                    ecoSaverLottieSuccess={ecoSaverLottieSuccess}
                />
            );
        case DELIVERY_SECTION_ELEMENT.SELF_DELIVERY:
            return <SelfDeliveryInstructions itemData={tabItem?.meta as RenderingDetails} />;
        default:
            return null;
    }
}

function DeliverySectionRenderer(props: DeliveryRendererProps) {
    const { renderingDetails, setCroutonVisibility, cartTopOffset } = props;

    const { onChange, tabOptions, error, loading, singleTabMeta } = useDeliveryRenderer(renderingDetails);

    const { deliveryInstructionsInfo, deliveryModeInfo } = useContext(DeliverySectionContext);
    const isPopCart = isPopFlow();
    const isToingCart = isToingFlow();

    const tabBarTheme = useMemo(() => (isToingCart ? TabBarTheme.ORANGE : TabBarTheme.NEUTRAL), [isToingCart]);

    const { ecoSaverLottieSuccess } = useEcoSaverCrouton({
        setCroutonVisibility: props.setCroutonVisibility,
    });

    const { customPadding } = useComputeTabContainerPadding();
    const { observerOptions, shouldPlayAnimation, onAnimationComplete, onDeliverySectionEnter } =
        useDeliveryModeLottie();

    const renderContent = (tabItem: ITabBarItem) => {
        // Moved this to a separate function, so no new function is created on every render
        return getWidget({
            tabItem,
            canShowDeliveryInstructionCrouton: deliveryInstructionsInfo.canShowDeliveryInstructionCrouton,
            setCroutonVisibility,
            cartTopOffset,
            ecoSaverLottieSuccess,
            ...deliveryModeInfo,
        });
    };

    const onTabChange = useCallback(
        (item) => {
            onChange(item.id);
        },
        [onChange],
    );

    const onError = useCallback((error?: Error, errorInfo?: React.ErrorInfo) => {
        const errorEvents = getNRErrorInfo(error, errorInfo);

        recordNREvent({
            eventType: NEWRELIC_TABLES.CHECKOUT_WEB_ERROR_BOUNDARY,
            widgetType: "DELIVERY_SECTION_RENDERER",
            ...errorEvents,
        });
    }, []);

    if (error) {
        recordNREvent({
            eventType: NEWRELIC_TABLES.EMPTY_DELIVERY_RENDERER_ERROR,
        });

        return null;
    }

    if (loading) {
        return <DeliverySectionShimmer />;
    }
    // Edge case: If there is only one tab, render it as a single tab
    if (tabOptions.length === 1) {
        return (
            <ErrorBoundary errorContent={null} errorFallback={onError}>
                <ElementObserver onViewEnter={onDeliverySectionEnter} options={observerOptions}>
                    <DeliveryRendererCartGroupContainer id={CART_ELEMENT_IDS.DELIVERY_SECTION_CONTAINER_ID}>
                        <SingleTab
                            renderContent={renderContent}
                            tabOptions={tabOptions}
                            isPopCart={isPopCart}
                            tabMeta={singleTabMeta}
                        />
                        {shouldPlayAnimation && (
                            <DeliveryModeLottie
                                animationUrl={DELIVERY_MODE_ANIMATION_URL}
                                shouldPlayAnimation={shouldPlayAnimation}
                                onAnimationComplete={onAnimationComplete}
                            />
                        )}
                    </DeliveryRendererCartGroupContainer>
                </ElementObserver>
            </ErrorBoundary>
        );
    }

    return (
        <ErrorBoundary errorContent={null} errorFallback={onError}>
            <ElementObserver onViewEnter={onDeliverySectionEnter} options={observerOptions}>
                <DeliveryRendererCartGroupContainer id={CART_ELEMENT_IDS.DELIVERY_SECTION_CONTAINER_ID}>
                    <TabContainer
                        customWrapperPadding={customPadding}
                        tabOptions={tabOptions}
                        onChange={onTabChange}
                        renderContent={renderContent}
                        tabBarTheme={tabBarTheme}
                    />
                    {shouldPlayAnimation && (
                        <DeliveryModeLottie
                            animationUrl={DELIVERY_MODE_ANIMATION_URL}
                            shouldPlayAnimation={shouldPlayAnimation}
                            onAnimationComplete={onAnimationComplete}
                        />
                    )}
                </DeliveryRendererCartGroupContainer>
            </ElementObserver>
        </ErrorBoundary>
    );
}

export default memo(DeliverySectionRenderer);
